import React, { useEffect, useState } from "react";
import {
    Button,
    Dropdown,
    Grid,
    Header,
    Icon,
    Label,
    Loader,
    Message,
    Modal,
    Pagination,
    Segment,
    Table,
} from "semantic-ui-react";
import { toast } from "react-toastify";
import { getProductOrderDetails } from "../../../services/reporting.service";
import {
    ProductMetric,
    ProductOrderDetailsResponse,
    ReportFilters,
    SortDirection,
} from "../../../types/models/Reporting";
import {
    formatCurrency,
    formatNumber,
    formatPercent,
    formatReportDate,
    orderStatusColor,
    orderStatusLabel,
} from "../reportingFormatters";

interface Props {
    product?: ProductMetric;
    filters: ReportFilters;
    onClose: () => void;
}

const ProductOrdersModal = ({ product, filters, onClose }: Props) => {
    const [data, setData] = useState<ProductOrderDetailsResponse>();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string>();
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(10);
    const [sortProperty, setSortProperty] = useState("outstandingBalance");
    const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

    useEffect(() => {
        if (!product) {
            setData(undefined);
            setError(undefined);
            return;
        }
        let mounted = true;
        const load = async () => {
            setLoading(true);
            setError(undefined);
            setData(undefined);
            try {
                const response = await getProductOrderDetails(product.productId, filters, {
                    page,
                    size,
                    sort: `${sortProperty},${sortDirection}`,
                });
                if (mounted) setData(response.data);
            } catch (error: any) {
                const message = error.message || "No fue posible consultar el desglose del producto.";
                if (mounted) {
                    setError(message);
                    toast.error(message);
                }
            } finally {
                if (mounted) setLoading(false);
            }
        };
        load();
        return () => { mounted = false; };
    }, [product, filters, page, size, sortProperty, sortDirection]);

    useEffect(() => setPage(0), [product, filters]);

    const changeSort = (property: string) => {
        setPage(0);
        setSortDirection((current) => sortProperty === property && current === "asc" ? "desc" : "asc");
        setSortProperty(property);
    };
    const sorted = (property: string) => sortProperty === property
        ? (sortDirection === "asc" ? "ascending" : "descending")
        : undefined;
    const summary = data?.summary;
    const summarySections = summary ? [
        {
            title: "Pedidos",
            icon: "boxes",
            rows: [
                ["Incluidos en esta vista", formatNumber(summary.orderCount)],
                ["Activos", formatNumber(summary.activeOrders)],
                ["Inconclusos", formatNumber(summary.incompleteOrders)],
                ["Terminados", formatNumber(summary.completedOrders)],
                ["Con pagos parciales", formatNumber(summary.partialPaymentOrders)],
                ["Garantías abiertas", formatNumber(summary.openGuarantees)],
            ],
        },
        {
            title: "Valor de pedidos",
            icon: "chart bar",
            rows: [
                ["Importe activo", formatCurrency(summary.activeTotalAmount)],
                ["Importe inconcluso", formatCurrency(summary.incompleteTotalAmount)],
                ["Importe terminado", formatCurrency(summary.completedTotalAmount)],
                ["Importe total", formatCurrency(summary.totalAmount)],
            ],
        },
        {
            title: "Movimientos",
            icon: "exchange",
            rows: [
                ["Anticipos", formatCurrency(summary.advanceAmount)],
                ["Abonos", formatCurrency(summary.installmentAmount)],
                ["Liquidaciones parciales", formatCurrency(summary.partialSettlementAmount)],
                ["Liquidaciones", formatCurrency(summary.fullSettlementAmount)],
            ],
        },
        {
            title: "Cobranza y saldo",
            icon: "money bill alternate outline",
            rows: [
                ["Total cobrado", formatCurrency(summary.paidAmount)],
                ["Saldo total", formatCurrency(summary.outstandingBalance)],
                ["Saldo activo", formatCurrency(summary.activeOutstandingBalance)],
            ],
        },
    ] : [];

    return (
        <Modal
            open={Boolean(product)}
            onClose={onClose}
            size="fullscreen"
            closeIcon
            closeOnDimmerClick
            className="dashboard-fullscreen-modal dashboard-product-modal"
        >
            <Modal.Header className="dashboard-product-modal__header">
                <Header as="h3">
                    <Icon name="shopping bag" />
                    <Header.Content>
                        {product?.name || "Producto"}
                        <Header.Subheader>Pedidos y cobranza al corte</Header.Subheader>
                    </Header.Content>
                </Header>
                <div className="dashboard-product-modal__labels">
                    <Label color="yellow"><Icon name="list alternate outline" />Detalle por producto</Label>
                    {product?.sku && <Label basic content={product.sku} />}
                </div>
            </Modal.Header>
            <Modal.Content scrolling className="dashboard-product-modal__content">
                <Message className="dashboard-product-view-message" icon>
                    <Icon name="info circle" />
                    <Message.Content>
                        <Message.Header>Qué incluye este desglose</Message.Header>
                        Muestra todos los pedidos del producto considerados por esta tabla; excluye borradores, validación y cancelados.
                        {data && <> Los totales y las filas corresponden al corte {formatReportDate(data.cutoff)}.</>}
                    </Message.Content>
                </Message>
                {summary && (
                    <Grid stackable doubling columns={4} className="dashboard-product-summary">
                        {summarySections.map((section) => (
                            <Grid.Column key={section.title}>
                                <Segment>
                                    <Header as="h4" dividing>
                                        <Icon name={section.icon as any} />
                                        <Header.Content>{section.title}</Header.Content>
                                    </Header>
                                    <Table basic="very" compact unstackable>
                                        <Table.Body>
                                            {section.rows.map(([label, value]) => (
                                                <Table.Row key={label}>
                                                    <Table.Cell>{label}</Table.Cell>
                                                    <Table.Cell textAlign="right"><strong>{value}</strong></Table.Cell>
                                                </Table.Row>
                                            ))}
                                        </Table.Body>
                                    </Table>
                                </Segment>
                            </Grid.Column>
                        ))}
                    </Grid>
                )}

                {error && <Message negative icon="warning circle" header="No fue posible cargar el desglose" content={error} />}
                {loading && (
                    <Segment basic placeholder className="dashboard-product-modal__loading">
                        <Loader active>Consultando réplica...</Loader>
                    </Segment>
                )}
                {!loading && data?.orders.length === 0 && (
                    <Message info icon="info circle" header="Sin pedidos" content="No hay pedidos incluidos para este producto y los filtros seleccionados." />
                )}
                {!loading && Boolean(data?.orders.length) && (
                    <Segment className="dashboard-product-orders">
                        <div className="dashboard-product-orders__heading">
                            <Header as="h4">
                                <Icon name="list alternate outline" />
                                <Header.Content>Pedidos del producto</Header.Content>
                            </Header>
                            <Label basic>{formatNumber(data?.totalElements)} pedidos</Label>
                        </div>
                        <div className="dashboard-table-scroll">
                            <Table sortable striped compact selectable className="dashboard-table dashboard-product-orders__table">
                            <Table.Header><Table.Row>
                                <Table.HeaderCell sorted={sorted("invoice")} onClick={() => changeSort("invoice")}>Factura</Table.HeaderCell>
                                <Table.HeaderCell sorted={sorted("createdAt")} onClick={() => changeSort("createdAt")}>Creación / etapa</Table.HeaderCell>
                                <Table.HeaderCell sorted={sorted("status")} onClick={() => changeSort("status")}>Estado</Table.HeaderCell>
                                <Table.HeaderCell sorted={sorted("clientName")} onClick={() => changeSort("clientName")}>Cliente</Table.HeaderCell>
                                <Table.HeaderCell sorted={sorted("advisorName")} onClick={() => changeSort("advisorName")}>Asesor</Table.HeaderCell>
                                <Table.HeaderCell sorted={sorted("paymentCount")} onClick={() => changeSort("paymentCount")} textAlign="right">Movimientos</Table.HeaderCell>
                                <Table.HeaderCell sorted={sorted("advanceAmount")} onClick={() => changeSort("advanceAmount")} textAlign="right">Anticipo</Table.HeaderCell>
                                <Table.HeaderCell sorted={sorted("installmentAmount")} onClick={() => changeSort("installmentAmount")} textAlign="right">Abonos</Table.HeaderCell>
                                <Table.HeaderCell sorted={sorted("partialSettlementAmount")} onClick={() => changeSort("partialSettlementAmount")} textAlign="right">Liq. parcial</Table.HeaderCell>
                                <Table.HeaderCell sorted={sorted("fullSettlementAmount")} onClick={() => changeSort("fullSettlementAmount")} textAlign="right">Liquidación</Table.HeaderCell>
                                <Table.HeaderCell sorted={sorted("totalAmount")} onClick={() => changeSort("totalAmount")} textAlign="right">Total</Table.HeaderCell>
                                <Table.HeaderCell sorted={sorted("paidAmount")} onClick={() => changeSort("paidAmount")} textAlign="right">Abonado</Table.HeaderCell>
                                <Table.HeaderCell sorted={sorted("outstandingBalance")} onClick={() => changeSort("outstandingBalance")} textAlign="right">Saldo</Table.HeaderCell>
                                <Table.HeaderCell textAlign="right">Cobranza</Table.HeaderCell>
                                <Table.HeaderCell />
                            </Table.Row></Table.Header>
                            <Table.Body>{data?.orders.map((order) => (
                                <Table.Row key={order.orderId}>
                                    <Table.Cell><strong>#{order.invoice || "—"}</strong></Table.Cell>
                                    <Table.Cell>
                                        {formatReportDate(order.createdAt)}
                                        <small>Etapa desde {formatReportDate(order.statusStartDate)}</small>
                                    </Table.Cell>
                                    <Table.Cell>
                                        <Label color={orderStatusColor(order.status)}>
                                            {orderStatusLabel(order.status, order.statusLabel)}
                                        </Label>
                                        <small>{order.active ? "En operación" : "Fuera del flujo activo"}</small>
                                    </Table.Cell>
                                    <Table.Cell>{order.clientName || "Sin cliente"}</Table.Cell>
                                    <Table.Cell>{order.advisorName || "Sin asignar"}</Table.Cell>
                                    <Table.Cell textAlign="right">{formatNumber(order.paymentCount)}</Table.Cell>
                                    <Table.Cell textAlign="right">{formatCurrency(order.advanceAmount)}</Table.Cell>
                                    <Table.Cell textAlign="right">{formatCurrency(order.installmentAmount)}</Table.Cell>
                                    <Table.Cell textAlign="right">{formatCurrency(order.partialSettlementAmount)}</Table.Cell>
                                    <Table.Cell textAlign="right">{formatCurrency(order.fullSettlementAmount)}</Table.Cell>
                                    <Table.Cell textAlign="right">{formatCurrency(order.totalAmount)}</Table.Cell>
                                    <Table.Cell textAlign="right">{formatCurrency(order.paidAmount)}</Table.Cell>
                                    <Table.Cell textAlign="right"><strong>{formatCurrency(order.outstandingBalance)}</strong></Table.Cell>
                                    <Table.Cell textAlign="right">
                                        <Label basic color={order.paymentProgressPercent >= 100 ? "green" : "orange"}>
                                            {formatPercent(order.paymentProgressPercent)}
                                        </Label>
                                    </Table.Cell>
                                    <Table.Cell collapsing>
                                        <Button
                                            as="a"
                                            secondary
                                            icon="search"
                                            content="Ver"
                                            size="mini"
                                            href={`/orders/${order.orderId}/details`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        />
                                    </Table.Cell>
                                </Table.Row>
                            ))}</Table.Body>
                            </Table>
                        </div>
                    </Segment>
                )}

                {data && data.totalElements > 0 && (
                    <div className="dashboard-pagination dashboard-pagination--split">
                        <span>{formatNumber(data.totalElements)} pedidos</span>
                        <Pagination
                            activePage={page + 1}
                            totalPages={Math.max(data.totalPages, 1)}
                            onPageChange={(_, pagination) => setPage(Number(pagination.activePage) - 1)}
                        />
                        <Dropdown
                            compact
                            selection
                            value={size}
                            options={[10, 25, 50].map((value) => ({ key: value, value, text: `${value} filas` }))}
                            onChange={(_, value) => { setPage(0); setSize(Number(value.value)); }}
                        />
                    </div>
                )}
            </Modal.Content>
            <Modal.Actions>
                <Button basic onClick={onClose}><Icon name="cancel" /> Cerrar</Button>
            </Modal.Actions>
        </Modal>
    );
};

export default ProductOrdersModal;
